import React, { ReactNode, useEffect, useState } from "react";
import ReactDOM from "react-dom/client";
import { createPortal } from "react-dom";
import { classNames } from "@/utils";
import "./index.less";

type Customizefn = {
  close: () => void;
};

type BaseProps = {
  title?: string;
  maskClose?: boolean;
  onRef?: (ref: { close: () => void }) => void;
  onClose?: () => void;
  onConfirm?: () => void;
  onCancel?: () => void;
  showFooter?: boolean;
};

type BaseContent = BaseProps & {
  customize?: false;
  content?: ReactNode;
};

type CustomizeContent = BaseProps & {
  customize: true;
  content?: (fn: Customizefn) => ReactNode;
};

export type ModalProps = BaseContent | CustomizeContent;

const Modal: React.FC<ModalProps> & {
  open: (props: ModalProps) => { close: () => void };
  confirm: (props: Omit<ModalProps, "onConfirm" | "onCancel" | "onClose">) => Promise<boolean>;
} = (props) => {
  const { title, content, maskClose = true, onRef, onClose, onConfirm, onCancel, showFooter = false, customize } = props;
  const [leave, setLeave] = useState(false);

  const beforeClose = () => {
    return new Promise((resolve) => {
      setLeave(true);
      setTimeout(() => {
        resolve(true);
      }, 300);
    });
  };

  const handleClose = async () => {
    await beforeClose();
    onClose?.();
  };

  const handleConfirm = async () => {
    await beforeClose();
    onConfirm?.();
  };

  const handleCancel = async () => {
    await beforeClose();
    onCancel?.();
  };

  const handleMaskClick = () => {
    if (maskClose) handleClose();
  };

  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", onEsc);
    onRef?.({ close: handleClose });
    return () => {
      window.removeEventListener("keydown", onEsc);
      onRef?.(null);
    };
  }, [onClose, onRef]);

  return createPortal(
    <div
      className={classNames({
        "modal-wrapper": true,
        "anim-leave": leave,
      })}
    >
      <div className="modal-mask" onClick={handleMaskClick} />
      <div className="modal-container">
        {customize ? (
          content({ close: handleClose })
        ) : (
          <div className="modal-content">
            <div className="modal-header">
              <span>{title}</span>
              <button className="modal-close" onClick={handleClose}>
                ×
              </button>
            </div>
            <div className="modal-body">{content as ReactNode}</div>
            {showFooter && (
              <div className="modal-footer">
                <button onClick={handleCancel}>取消</button>
                <button onClick={handleConfirm}>确认</button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

// =======================
// 🔽 静态方法实现部分
// =======================
function createModal(props: ModalProps) {
  const container = document.createElement("div");
  document.body.appendChild(container);
  const root = ReactDOM.createRoot(container);

  let modalRef: { close: () => void } | null = null;

  const handleClose = () => {
    root.unmount();
    container.remove();
    props.onClose?.();
  };

  const handleConfirm = () => {
    root.unmount();
    container.remove();
    props.onConfirm?.();
  };

  const handleCancel = () => {
    root.unmount();
    container.remove();
    props.onCancel?.();
  };

  root.render(
    <Modal
      {...props}
      onRef={(ref) => {
        modalRef = ref;
      }}
      onClose={handleClose}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
    />,
  );

  return {
    close: () => {
      modalRef?.close?.(); // 触发 Modal 内部动画
    },
  };
}

// Modal.open 静态方法
Modal.open = (props: ModalProps) => {
  return createModal(props);
};

// Modal.confirm 静态方法（返回 Promise）
Modal.confirm = (props: Omit<ModalProps, "onClose" | "onConfirm" | "onCancel">): Promise<boolean> => {
  return new Promise((resolve) => {
    createModal({
      ...props,
      showFooter: true,
      onConfirm: () => resolve(true),
      onCancel: () => resolve(false),
      onClose: () => resolve(false),
    } as ModalProps);
  });
};

export default Modal;
